"use client";
/**
 * BulkOrderActions.jsx
 *
 * Drop-in bulk-action system for MaterialsTab3.
 *
 * Exports:
 *   - BulkOrderActions   (the floating action bar)
 *   - BulkDeliverModal   (multi-order delivery entry)
 *   - BulkEmailModal     (per-vendor consolidated email + PDF)
 *
 * Usage in MaterialsTab3:
 *   1. Add selectedOrderIds (Set) and setSelectedOrderIds state.
 *   2. Render checkboxes in the orders table rows.
 *   3. Mount <BulkOrderActions> below the table.
 *   4. Pass the required props (see PropTypes comments below).
 */

import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

// ─────────────────────────────────────────────────────────────────────────────
// SelectAllCheckbox — header checkbox for the orders table
// ─────────────────────────────────────────────────────────────────────────────
export function SelectAllCheckbox({
  orders,
  selectedOrderIds,
  setSelectedOrderIds,
  eligibleStatuses = ["confirmed", "partial", "pending"],
}) {
  const eligible = orders.filter((o) => eligibleStatuses.includes(o.status));
  const allSelected =
    eligible.length > 0 &&
    eligible.every((o) => selectedOrderIds.has(o._id?.toString()));
  const someSelected =
    !allSelected &&
    eligible.some((o) => selectedOrderIds.has(o._id?.toString()));

  const toggle = () => {
    if (allSelected) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(eligible.map((o) => o._id?.toString())));
    }
  };

  return (
    <input
      type="checkbox"
      checked={allSelected}
      ref={(el) => el && (el.indeterminate = someSelected)}
      onChange={toggle}
      title="Select all confirmable orders"
      className="h-3.5 w-3.5 rounded border-gray-500 accent-orange-500 cursor-pointer"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RowCheckbox — per-row checkbox
// ─────────────────────────────────────────────────────────────────────────────
export function RowCheckbox({ order, selectedOrderIds, setSelectedOrderIds }) {
  const eligible = ["confirmed", "partial", "pending"].includes(order.status);
  if (!eligible) return <span className="w-3.5 inline-block" />;

  const checked = selectedOrderIds.has(order._id?.toString()); // ← toString()
  const toggle = (e) => {
    e.stopPropagation();
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      const id = order._id?.toString(); // ← toString()
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={toggle}
      onClick={(e) => e.stopPropagation()}
      className="h-3.5 w-3.5 rounded border-gray-500 accent-orange-500 cursor-pointer"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BulkDeliverModal
// Props:
//   orders           — full order objects for selected IDs
//   token
//   onDelivered(updatedOrders)
//   onClose()
// ─────────────────────────────────────────────────────────────────────────────
export function BulkDeliverModal({ orders, token, onDelivered, onClose }) {
  // deliveryState: { [orderId]: { [itemId]: qty } }
  const [deliveryState, setDeliveryState] = useState(() => {
    const s = {};
    orders.forEach((o) => {
      s[o._id] = {};
      (o.items || []).forEach((i) => {
        s[o._id][i._id] = i.qtyPending || 0;
      });
    });
    return s;
  });

  const [varianceReasons, setVarianceReasons] = useState({});
  const [delivering, setDelivering] = useState(false);
  const [error, setError] = useState("");

  const deliverable = orders.filter((o) =>
    ["confirmed", "partial"].includes(o.status),
  );

  const setQty = (orderId, itemId, val) =>
    setDeliveryState((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], [itemId]: val },
    }));

  const setAllFull = () => {
    const s = {};
    deliverable.forEach((o) => {
      s[o._id] = {};
      (o.items || []).forEach((i) => {
        s[o._id][i._id] = i.qtyPending || 0;
      });
    });
    setDeliveryState(s);
  };

  const handleDeliver = async () => {
    setError("");
    const payload = deliverable
      .map((o) => ({
        orderId: o._id,
        items: (o.items || [])
          .map((i) => ({
            itemId: i._id,
            qtyDelivered: parseFloat(deliveryState[o._id]?.[i._id]) || 0,
            varianceReason: varianceReasons[`${o._id}-${i._id}`] || undefined,
          }))
          .filter((i) => i.qtyDelivered > 0),
      }))
      .filter((o) => o.items.length > 0);

    if (!payload.length) {
      setError("Enter at least one delivered quantity.");
      return;
    }

    setDelivering(true);
    try {
      const res = await axios.post(
        `${API}/materialOrders3/bulk-deliver`,
        { orders: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onDelivered(res.data.orders);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setDelivering(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-white">
              Bulk Record Delivery
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {deliverable.length} order{deliverable.length !== 1 ? "s" : ""} ·
              Enter quantities delivered
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={setAllFull}
              className="px-3 py-1.5 text-xs font-medium bg-teal-700 hover:bg-teal-600 text-white rounded-lg transition-colors"
            >
              Set All Full
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {error && (
            <div className="px-3 py-2 bg-red-900/50 border border-red-700 rounded-lg text-xs text-red-300">
              {error}
            </div>
          )}

          {deliverable.map((order) => (
            <div key={order._id} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono font-semibold text-orange-400">
                  MO #{order.orderNumber}
                </span>
                <span className="text-xs text-gray-500">
                  · {order.vendor?.companyName || "No vendor"}
                </span>
                <span className="text-xs text-gray-600">
                  · PO #{order.purchaseOrder?.poNumber}
                </span>
              </div>

              <div className="rounded-lg border border-gray-700 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-400">
                        Item
                      </th>
                      <th className="px-3 py-2 text-left text-gray-400">
                        Size
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400">
                        Ordered
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400">
                        Delivered
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400">
                        Pending
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400 w-24">
                        Deliver Now
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {(order.items || []).map((item, i) => {
                      const deliverNow =
                        parseFloat(deliveryState[order._id]?.[item._id]) || 0;
                      const showVariance =
                        deliverNow < (item.qtyPending || 0) && deliverNow > 0;
                      return (
                        <tr
                          key={item._id}
                          className={
                            i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                          }
                        >
                          <td className="px-3 py-2">
                            <p className="text-gray-200 leading-tight">
                              {item.description}
                            </p>
                            <p className="text-[10px] font-mono text-orange-400">
                              {item.itemCode}
                            </p>
                          </td>
                          <td className="px-3 py-2 text-gray-400">
                            {item.size || "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-300">
                            {item.quantity || 0}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-green-300">
                            {item.qtyDelivered || 0}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-yellow-300">
                            {item.qtyPending || 0}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col items-end gap-1">
                              <input
                                type="number"
                                value={deliverNow}
                                min="0"
                                max={item.qtyPending || 0}
                                onChange={(e) =>
                                  setQty(order._id, item._id, e.target.value)
                                }
                                className="w-20 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none"
                              />
                              {showVariance && (
                                <input
                                  type="text"
                                  placeholder="Variance reason…"
                                  value={
                                    varianceReasons[
                                      `${order._id}-${item._id}`
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    setVarianceReasons((v) => ({
                                      ...v,
                                      [`${order._id}-${item._id}`]:
                                        e.target.value,
                                    }))
                                  }
                                  className="w-32 px-2 py-1 text-[10px] bg-gray-700 border border-yellow-700/50 rounded text-gray-400 focus:border-orange-500 focus:outline-none"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {orders.filter((o) => !["confirmed", "partial"].includes(o.status))
            .length > 0 && (
            <p className="text-xs text-gray-600">
              {
                orders.filter(
                  (o) => !["confirmed", "partial"].includes(o.status),
                ).length
              }{" "}
              selected order(s) skipped — not in a deliverable status.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-700 shrink-0">
          <p className="text-xs text-gray-500">
            Delivering across {deliverable.length} order
            {deliverable.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleDeliver}
              disabled={delivering}
              className={cls(
                "px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
                delivering
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700",
              )}
            >
              {delivering ? "Recording…" : "Record Delivery"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BulkSiteUpdateModal
//
// Sends ONE internal-only update (no vendor email) covering all selected
// orders. Requires same-vendor selection (enforced by BulkActionsBar gating,
// same as BulkEmailModal). Generates ONE combined site PDF across all orders
// and lets the user write a single custom update message.
//
// Props:
//   orders           — full order objects for selected IDs (same vendor)
//   project          — project object
//   companyProfile   — for logo/name
//   vendors          — all vendor accounts
//   contacts         — all contacts
//   users            — all users (for field contacts etc.)
//   token
//   userEmail
//   onSent()
//   onClose()
// ─────────────────────────────────────────────────────────────────────────────
export function BulkSiteUpdateModal({
  orders,
  project,
  companyProfile,
  vendors,
  contacts,
  users,
  token,
  userEmail,
  onSent,
  onClose,
}) {
  const vendorId = orders[0]?.vendor?._id?.toString() || "__none__";
  const vendorName = orders[0]?.vendor?.companyName || "No Vendor";
  const orderNums = orders.map((o) => `MO#${o.orderNumber}`).join(", ");

  const [subject, setSubject] = useState(
    `Update: ${project?.projectName || "Project"} — ${orderNums}`,
  );
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Generate ONE combined site PDF across all selected orders (same
  // structure as the group site PDF in BulkEmailModal's generateGroupPDFs).
  const generateCombinedSitePdf = useCallback(async () => {
    const siteTemplateRes = await fetch("/material_order_template2_site.docx");
    if (!siteTemplateRes.ok)
      throw new Error("Could not load site PDF template");
    const siteTemplateBuffer = await siteTemplateRes.arrayBuffer();

    const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
    const validBase64 =
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    const base64Parser = (tagValue) => {
      if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
        return null;
      const s = tagValue.replace(base64Regex, "");
      if (!validBase64.test(s)) throw new Error("Invalid base64");
      const bin = window.atob(s);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes.buffer;
    };

    let imageBase64 = null;
    if (companyProfile?.logo) {
      if (companyProfile.logo.startsWith("data:image")) {
        imageBase64 = companyProfile.logo;
      } else {
        try {
          const logoRes = await fetch(companyProfile.logo);
          if (logoRes.ok) {
            const blob = await logoRes.blob();
            imageBase64 = await new Promise((res) => {
              const r = new FileReader();
              r.onloadend = () => res(r.result);
              r.readAsDataURL(blob);
            });
          }
        } catch {}
      }
    }

    const vendor = vendors.find((v) => v._id.toString() === vendorId) || {};
    const projectLocation = project?.address
      ? [
          project.address.street,
          project.address.city,
          `${project.address.state} ${project.address.postalCode}`,
        ]
          .filter(Boolean)
          .join(", ")
      : project?.location || "N/A";

    const companyName = companyProfile?.companyName || "Your Company";
    const companyAddress = companyProfile?.address
      ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
      : "N/A";
    const companyPhone = companyProfile?.phone || "N/A";
    const vendorAddress = vendor.address
      ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}`
      : "N/A";

    const fmtDate = (d) =>
      d
        ? new Date(d).toLocaleDateString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A";

    const siteItems = [];
    for (const order of orders) {
      siteItems.push({
        description: `── MO #${order.orderNumber} · PO #${order.purchaseOrder?.poNumber || "—"} ${order.purchaseOrder?.title || ""}`,
        size: "",
        uom: "",
        quantity: "",
        unitPrice: "",
        lineTotal: "",
      });
      for (const i of (order.items || []).filter(
        (i) => (i.quantity || 0) > 0,
      )) {
        const isCov = isCoverageBased(i.uom);
        siteItems.push({
          description: `${i.description || ""}`.trim(),
          size: i.size || "—",
          uom: isCov ? "PC" : i.uom || "—",
          quantity: String(i.quantity || 0),
          unitPrice: "",
          lineTotal: "",
        });
      }
    }

    const allContactIds = new Set();
    orders.forEach((o) => {
      (Array.isArray(o.contact) ? o.contact : []).forEach((c) =>
        allContactIds.add((c._id || c).toString()),
      );
    });
    const vendorContactsText =
      contacts
        .filter((c) => allContactIds.has(c._id.toString()))
        .map(
          (c) =>
            `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
        )
        .join(", ") || "N/A";

    const allFieldIds = new Set();
    orders.forEach((o) => {
      (Array.isArray(o.fieldContact) ? o.fieldContact : []).forEach((u) =>
        allFieldIds.add((u._id || u).toString()),
      );
    });
    const fieldContactNames =
      users
        .filter((u) => allFieldIds.has(u._id.toString()))
        .map((u) => {
          const name = u.displayName || u.email || "User";
          return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
        })
        .join(", ") || "N/A";

    const earliestDelivery =
      orders
        .map((o) => o.expectedDeliveryDate)
        .filter(Boolean)
        .sort()[0] || null;

    const zip = new PizZip(siteTemplateBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      modules: [
        new ImageModule({ getImage: base64Parser, getSize: () => [125, 90] }),
      ],
    });
    doc.setData({
      companyName,
      companyAddress,
      companyPhone,
      projectName: project?.projectName || "Project",
      projectLocation,
      vendorName: vendor.companyName || vendorName,
      vendorAddress,
      vendorContacts: vendorContactsText,
      materialOrderNumber: orders.map((o) => o.orderNumber).join(", "),
      poNumber: String(project?.projectNumber || ""),
      createdAt: fmtDate(new Date()),
      expectedDeliveryDate: fmtDate(earliestDelivery),
      shippingMethod: orders[0]?.shippingMethod || "Delivery",
      deliveryInstructions:
        orders
          .map((o) => o.deliveryInstructions)
          .filter(Boolean)
          .join(" / ") || "N/A",
      revision: "",
      image: imageBase64,
      fieldContacts: fieldContactNames,
      deliveryContact: "N/A",
      items: siteItems,
      subtotal: "",
      taxRate: "",
      taxAmount: "",
      grandTotal: "",
    });
    doc.render();

    const docxBlob = doc.getZip().generate({ type: "blob" });
    const formData = new FormData();
    formData.append("docx", docxBlob, `BULK_${vendorId}_SITE_UPDATE.docx`);
    formData.append("documentType", "MaterialOrder");
    formData.append("identifier", `BULK_${vendorId}_SITE_UPDATE`);

    const pdfRes = await axios.post(`${API}/upload/convert-to-pdf`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    if (!pdfRes.headers["content-type"]?.includes("application/pdf"))
      throw new Error("PDF conversion failed for combined site update");

    return new Blob([pdfRes.data], { type: "application/pdf" });
  }, [
    orders,
    project,
    companyProfile,
    vendors,
    contacts,
    users,
    token,
    vendorId,
    vendorName,
  ]);

  const handleSend = async () => {
    if (!message.trim()) {
      setError("Enter an update message to send.");
      return;
    }
    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }

    setSending(true);
    setError("");
    try {
      const sitePdf = await generateCombinedSitePdf();

      const safeProject = (project?.projectName || "Project")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim();
      const orderNumsFile = orders.map((o) => o.orderNumber).join(", ");

      const formData = new FormData();
      formData.append("from", userEmail);
      formData.append("subject", subject);
      formData.append("message", message);
      formData.append("orderIds", JSON.stringify(orders.map((o) => o._id)));
      formData.append(
        "siteFile",
        sitePdf,
        `MaterialOrders-${orderNumsFile} - ${safeProject} - SITE UPDATE.pdf`,
      );

      const res = await axios.post(
        `${API}/materialOrders3/bulk-send-site-update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setDone(true);
      setTimeout(() => onSent(res.data), 1200);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-cyan-400">
              Send Site Update
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {orders.length} order{orders.length !== 1 ? "s" : ""} ·{" "}
              {vendorName} · {orderNums}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-white text-xl leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-900/50 border border-red-700 rounded-lg text-xs text-red-300">
              {error}
            </div>
          )}
          {done && (
            <div className="px-3 py-2 bg-green-900/50 border border-green-700 rounded-lg text-xs text-green-300">
              ✓ Site update sent
            </div>
          )}

          <p className="text-xs text-gray-500">
            Goes to field contacts, PM, foreman, admins, and delivery driver (if
            pickup) across all selected orders. One combined site PDF is
            attached. Vendor is not emailed.
          </p>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">
              Subject *
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">
              Update Message *
            </label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Delivery pushed to Friday across these orders…"
              className="w-full px-3 py-2 text-sm rounded border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500 focus:border-cyan-500 focus:outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-700 shrink-0">
          <p className="text-xs text-gray-500">
            {sending
              ? "Generating PDF & sending…"
              : "1 combined email will be sent"}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || done}
              className={cls(
                "px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
                sending || done
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-cyan-600 hover:bg-cyan-700",
              )}
            >
              {sending ? "Sending…" : done ? "Sent ✓" : "Send Update"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BulkEmailModal
//
// Groups selected orders by vendor. For each vendor group:
//   - Generates ONE vendor PDF with all orders' items merged
//   - Generates ONE site PDF similarly
//   - Sends one email to vendor contacts on all orders in that group
//   - Sends one internal site email
//
// Props:
//   orders           — full order objects for selected IDs (any status except void)
//   project          — project object
//   companyProfile   — for logo/name
//   vendors          — all vendor accounts
//   contacts         — all contacts
//   users            — all users (for field contacts etc.)
//   token
//   userEmail
//   onSent()
//   onClose()
// ─────────────────────────────────────────────────────────────────────────────
export function BulkEmailModal({
  orders,
  project,
  companyProfile,
  vendors,
  contacts,
  users,
  token,
  userEmail,
  onSent,
  onClose,
}) {
  // Group orders by vendor
  const vendorGroups = useMemo(() => {
    const groups = {};
    orders.forEach((o) => {
      const vid = o.vendor?._id?.toString() || "__none__";
      if (!groups[vid]) {
        groups[vid] = {
          vendorId: vid,
          vendorName: o.vendor?.companyName || "No Vendor",
          orders: [],
        };
      }
      groups[vid].orders.push(o);
    });
    return Object.values(groups);
  }, [orders]);

  // Per-vendor email form state: { [vendorId]: { to, subject, body, signature } }
  const [emailForms, setEmailForms] = useState(() => {
    const forms = {};
    vendorGroups.forEach((g) => {
      // Collect all unique contact emails for this vendor group
      const allContactIds = new Set();
      g.orders.forEach((o) => {
        (Array.isArray(o.contact) ? o.contact : []).forEach((c) => {
          allContactIds.add((c._id || c).toString());
        });
      });
      const vendorContacts = contacts.filter((c) =>
        allContactIds.has(c._id.toString()),
      );
      const toEmails = [
        ...new Set(vendorContacts.map((c) => c.email).filter(Boolean)),
      ].join(", ");

      const greeting = vendorContacts[0]?.firstName
        ? `Hi ${vendorContacts[0].firstName},`
        : `Dear ${g.vendorName},`;

      const orderNums = g.orders.map((o) => `MO#${o.orderNumber}`).join(", ");

      forms[g.vendorId] = {
        to: toEmails,
        subject: `${project?.projectName} — Material Order${g.orders.length > 1 ? "s" : ""} ${orderNums}`,
        body: `${greeting}\n\nPlease find attached the material order${g.orders.length > 1 ? "s" : ""} (${orderNums}) for your review and action.\n\nBest regards,`,
        signature: userEmail || "",
      };
    });
    return forms;
  });

  const updateForm = (vendorId, field, value) =>
    setEmailForms((prev) => ({
      ...prev,
      [vendorId]: { ...prev[vendorId], [field]: value },
    }));

  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(""); // status message
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Generate PDFs for one vendor group (all orders merged into one doc each)
  // Generate PDFs for one vendor group.
  //   vendor side → ONE PDF PER ORDER (each with its own MO number/totals)
  //   site side   → ONE combined PDF for the whole group (helps the field crew)
  const generateGroupPDFs = useCallback(
    async (group) => {
      const [vendorTemplateRes, siteTemplateRes] = await Promise.all([
        fetch("/material_order_template2.docx"),
        fetch("/material_order_template2_site.docx"),
      ]);
      if (!vendorTemplateRes.ok)
        throw new Error("Could not load vendor PDF template");
      if (!siteTemplateRes.ok)
        throw new Error("Could not load site PDF template");

      const vendorTemplateBuffer = await vendorTemplateRes.arrayBuffer();
      const siteTemplateBuffer = await siteTemplateRes.arrayBuffer();

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      const base64Parser = (tagValue) => {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const s = tagValue.replace(base64Regex, "");
        if (!validBase64.test(s)) throw new Error("Invalid base64");
        const bin = window.atob(s);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes.buffer;
      };

      let imageBase64 = null;
      if (companyProfile?.logo) {
        if (companyProfile.logo.startsWith("data:image")) {
          imageBase64 = companyProfile.logo;
        } else {
          try {
            const logoRes = await fetch(companyProfile.logo);
            if (logoRes.ok) {
              const blob = await logoRes.blob();
              imageBase64 = await new Promise((res) => {
                const r = new FileReader();
                r.onloadend = () => res(r.result);
                r.readAsDataURL(blob);
              });
            }
          } catch {}
        }
      }

      const vendor =
        vendors.find((v) => v._id.toString() === group.vendorId) || {};

      const projectLocation = project?.address
        ? [
            project.address.street,
            project.address.city,
            `${project.address.state} ${project.address.postalCode}`,
          ]
            .filter(Boolean)
            .join(", ")
        : project?.location || "N/A";

      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate || 0);

      const companyName = companyProfile?.companyName || "Your Company";
      const companyAddress = companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
        : "N/A";
      const companyPhone = companyProfile?.phone || "N/A";
      const vendorAddress = vendor.address
        ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}`
        : "N/A";

      const fmtDate = (d) =>
        d
          ? new Date(d).toLocaleDateString("en-US", {
              timeZone: "UTC",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "N/A";

      // ── Shared renderer ──────────────────────────────────────────────────
      const renderToPdf = async (templateBuffer, data, label) => {
        const zip = new PizZip(templateBuffer);
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
          modules: [
            new ImageModule({
              getImage: base64Parser,
              getSize: () => [125, 90],
            }),
          ],
        });
        doc.setData(data);
        doc.render();

        const docxBlob = doc.getZip().generate({ type: "blob" });
        const formData = new FormData();
        formData.append("docx", docxBlob, `${label}.docx`);
        formData.append("documentType", "MaterialOrder");
        formData.append("identifier", label);

        const pdfRes = await axios.post(
          `${API}/upload/convert-to-pdf`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );
        if (!pdfRes.headers["content-type"]?.includes("application/pdf"))
          throw new Error(`PDF conversion failed (${label})`);
        return new Blob([pdfRes.data], { type: "application/pdf" });
      };

      // ── Per-order field/contact helpers ──────────────────────────────────
      const orderVendorContactsText = (order) => {
        const ids = new Set(
          (Array.isArray(order.contact) ? order.contact : []).map((c) =>
            (c._id || c).toString(),
          ),
        );
        const vc = contacts.filter((c) => ids.has(c._id.toString()));
        return (
          vc
            .map(
              (c) =>
                `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
            )
            .join(", ") || "N/A"
        );
      };

      const orderFieldContactNames = (order) => {
        const ids = new Set(
          (Array.isArray(order.fieldContact) ? order.fieldContact : []).map(
            (u) => (u._id || u).toString(),
          ),
        );
        const fc = users.filter((u) => ids.has(u._id.toString()));
        return (
          fc
            .map((u) => {
              const name = u.displayName || u.email || "User";
              return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
            })
            .join(", ") || "N/A"
        );
      };

      // ── VENDOR PDFs — one per order ──────────────────────────────────────
      const vendorPdfs = [];
      for (const order of group.orders) {
        const revisionLabel = ["delivered", "void"].includes(order.status)
          ? "FINAL"
          : order.revision > 0
            ? `R${order.revision}`
            : "";

        const items = [];
        for (const i of (order.items || []).filter(
          (i) => (i.quantity || 0) > 0,
        )) {
          const isCov = isCoverageBased(i.uom);
          const cov = i.coveragePerUnit || 1;
          const rate =
            isCov && cov > 0 ? (i.unitPrice || 0) / cov : i.unitPrice || 0;
          items.push({
            description: `${i.description || ""}`.trim(),
            size: i.size || "—",
            uom: isCov ? "PC" : i.uom || "—",
            quantity: String(i.quantity || 0),
            unitPrice: isCov
              ? `$${fmt(i.unitPrice || 0)} (${cov} ${i.uom}/PC @ $${fmt(rate)}/${i.uom})`
              : `$${fmt(i.unitPrice || 0)}`,
            lineTotal: `$${fmt(i.total || 0)}`,
          });
        }
        (order.surcharges || []).forEach((s) => {
          const amt = parseFloat(s.price || s.amount) || 0;
          if (amt !== 0) {
            items.push({
              description: s.description || "Surcharge",
              size: "—",
              uom: "—",
              quantity: "1",
              unitPrice: `$${fmt(amt)}`,
              lineTotal: `$${fmt(amt)}`,
            });
          }
        });

        const itemsTotal = (order.items || []).reduce(
          (s, i) => s + (i.total || 0),
          0,
        );
        const surchargesTotal = (order.surcharges || []).reduce(
          (s, sc) => s + (parseFloat(sc.price || sc.amount || 0) || 0),
          0,
        );
        const subtotal = itemsTotal + surchargesTotal;
        const tax = subtotal * (taxRate / 100);
        const grandTotal = subtotal + tax;

        const vendorPdf = await renderToPdf(
          vendorTemplateBuffer,
          {
            companyName,
            companyAddress,
            companyPhone,
            projectName: project?.projectName || "Project",
            projectLocation,
            vendorName: vendor.companyName || group.vendorName,
            vendorAddress,
            vendorContacts: orderVendorContactsText(order),
            materialOrderNumber: order.orderNumber || "MO#",
            poNumber: String(project?.projectNumber || ""),
            createdAt: fmtDate(order.createdAt),
            expectedDeliveryDate: fmtDate(order.expectedDeliveryDate),
            shippingMethod: order.shippingMethod || "Delivery",
            deliveryInstructions: order.deliveryInstructions || "N/A",
            revision: revisionLabel,
            image: imageBase64,
            fieldContacts: orderFieldContactNames(order),
            deliveryContact: "N/A",
            items,
            subtotal: `$${fmt(subtotal)}`,
            taxRate: taxRate > 0 ? `${taxRate}%` : "0%",
            taxAmount: `$${fmt(tax)}`,
            grandTotal: `$${fmt(grandTotal)}`,
          },
          `MO_${order.orderNumber}_VENDOR`,
        );

        vendorPdfs.push({ orderNumber: order.orderNumber, blob: vendorPdf });
      }

      // ── SITE PDF — one combined doc for the whole group ──────────────────
      const siteItems = [];
      for (const order of group.orders) {
        siteItems.push({
          description: `── MO #${order.orderNumber} · PO #${order.purchaseOrder?.poNumber || "—"} ${order.purchaseOrder?.title || ""}`,
          size: "",
          uom: "",
          quantity: "",
          unitPrice: "",
          lineTotal: "",
        });
        for (const i of (order.items || []).filter(
          (i) => (i.quantity || 0) > 0,
        )) {
          const isCov = isCoverageBased(i.uom);
          siteItems.push({
            description: `${i.description || ""}`.trim(),
            size: i.size || "—",
            uom: isCov ? "PC" : i.uom || "—",
            quantity: String(i.quantity || 0),
            unitPrice: "",
            lineTotal: "",
          });
        }
      }

      const allContactIds = new Set();
      group.orders.forEach((o) => {
        (Array.isArray(o.contact) ? o.contact : []).forEach((c) =>
          allContactIds.add((c._id || c).toString()),
        );
      });
      const vendorContactsText =
        contacts
          .filter((c) => allContactIds.has(c._id.toString()))
          .map(
            (c) =>
              `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
          )
          .join(", ") || "N/A";

      const allFieldIds = new Set();
      group.orders.forEach((o) => {
        (Array.isArray(o.fieldContact) ? o.fieldContact : []).forEach((u) =>
          allFieldIds.add((u._id || u).toString()),
        );
      });
      const fieldContactNames =
        users
          .filter((u) => allFieldIds.has(u._id.toString()))
          .map((u) => {
            const name = u.displayName || u.email || "User";
            return u.phoneNumber ? `${name} (${u.phoneNumber})` : name;
          })
          .join(", ") || "N/A";

      const earliestDelivery =
        group.orders
          .map((o) => o.expectedDeliveryDate)
          .filter(Boolean)
          .sort()[0] || null;

      const sitePdf = await renderToPdf(
        siteTemplateBuffer,
        {
          companyName,
          companyAddress,
          companyPhone,
          projectName: project?.projectName || "Project",
          projectLocation,
          vendorName: vendor.companyName || group.vendorName,
          vendorAddress,
          vendorContacts: vendorContactsText,
          materialOrderNumber: group.orders
            .map((o) => o.orderNumber)
            .join(", "),
          poNumber: String(project?.projectNumber || ""),
          createdAt: fmtDate(new Date()),
          expectedDeliveryDate: fmtDate(earliestDelivery),
          shippingMethod: group.orders[0]?.shippingMethod || "Delivery",
          deliveryInstructions:
            group.orders
              .map((o) => o.deliveryInstructions)
              .filter(Boolean)
              .join(" / ") || "N/A",
          revision: "",
          image: imageBase64,
          fieldContacts: fieldContactNames,
          deliveryContact: "N/A",
          items: siteItems,
          subtotal: "",
          taxRate: "",
          taxAmount: "",
          grandTotal: "",
        },
        `BULK_${group.vendorId}_SITE`,
      );

      return { vendorPdfs, sitePdf };
    },
    [companyProfile, contacts, project, token, users, vendors],
  );

  const handleSend = async () => {
    setSending(true);
    setError("");
    let sentCount = 0;

    try {
      for (const group of vendorGroups) {
        setProgress(
          `Generating PDFs for ${group.vendorName} (${sentCount + 1}/${vendorGroups.length})…`,
        );

        const { vendorPdfs, sitePdf } = await generateGroupPDFs(group);
        const form = emailForms[group.vendorId];

        if (!form.to || !form.subject) {
          setError(`Missing To or Subject for ${group.vendorName}`);
          setSending(false);
          return;
        }

        setProgress(
          `Sending email to ${group.vendorName} (${sentCount + 1}/${vendorGroups.length})…`,
        );

        const safeProject = (project?.projectName || "Project")
          .replace(/[^a-zA-Z0-9-_ ]/g, "")
          .trim();

        const formData = new FormData();
        formData.append("to", form.to);
        formData.append("subject", form.subject);
        formData.append("body", form.body);
        formData.append("signature", form.signature || "");
        formData.append("from", userEmail);

        // Explicit orderId -> fieldname map so the backend never has to
        // guess which PDF belongs to which order (filename parsing is
        // fragile if naming conventions ever change).
        const orderIds = group.orders.map((o) => o._id);
        const fileMap = {};

        vendorPdfs.forEach(({ orderNumber, blob }) => {
          const order = group.orders.find((o) => o.orderNumber === orderNumber);
          if (!order) return;
          const fieldname = `vendorFile_${order._id}`;
          fileMap[order._id] = fieldname;
          formData.append(
            fieldname,
            blob,
            `MaterialOrder-MO${orderNumber} - ${safeProject}.pdf`,
          );
        });

        formData.append("orderIds", JSON.stringify(orderIds));
        formData.append("fileMap", JSON.stringify(fileMap));

        // One combined site PDF for the whole group — uploaded once
        // server-side and attached to every order in the group, so every
        // order's attachments/timeline stay consistent with each other.
        const orderNums = group.orders.map((o) => o.orderNumber).join(", ");
        formData.append(
          "siteFiles",
          sitePdf,
          `MaterialOrders-${orderNums} - ${safeProject} - SITE.pdf`,
        );

        await axios.post(`${API}/materialOrders3/bulk-send-email`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        sentCount++;
      }

      setProgress(
        `Sent ${sentCount} email${sentCount !== 1 ? "s" : ""} successfully.`,
      );
      setDone(true);
      setTimeout(() => onSent(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setSending(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-white">
              Bulk Send Material Orders
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {orders.length} order{orders.length !== 1 ? "s" : ""} ·{" "}
              {vendorGroups.length} vendor group
              {vendorGroups.length !== 1 ? "s" : ""}
              {" — "}orders from the same vendor are merged into one email
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-white text-xl leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {error && (
            <div className="px-3 py-2 bg-red-900/50 border border-red-700 rounded-lg text-xs text-red-300">
              {error}
            </div>
          )}

          {done && (
            <div className="px-3 py-2 bg-green-900/50 border border-green-700 rounded-lg text-xs text-green-300">
              ✓ {progress}
            </div>
          )}

          {vendorGroups.map((group) => {
            const form = emailForms[group.vendorId];
            return (
              <div
                key={group.vendorId}
                className="space-y-3 rounded-xl border border-gray-700 p-4 bg-gray-900/40"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-orange-400">
                      {group.vendorName}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {group.orders.length} order
                      {group.orders.length !== 1 ? "s" : ""} ·{" "}
                      {group.orders
                        .map((o) => `MO#${o.orderNumber}`)
                        .join(", ")}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {group.orders.map((o) => (
                      <span
                        key={o._id}
                        className="text-[9px] font-mono bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded"
                      >
                        PO#{o.purchaseOrder?.poNumber}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    From
                  </label>
                  <input
                    value={userEmail}
                    disabled
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-600 bg-gray-700/50 text-gray-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    To *
                  </label>
                  <input
                    value={form.to}
                    onChange={(e) =>
                      updateForm(group.vendorId, "to", e.target.value)
                    }
                    placeholder="vendor@example.com"
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    Subject *
                  </label>
                  <input
                    value={form.subject}
                    onChange={(e) =>
                      updateForm(group.vendorId, "subject", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    Body
                  </label>
                  <textarea
                    rows={4}
                    value={form.body}
                    onChange={(e) =>
                      updateForm(group.vendorId, "body", e.target.value)
                    }
                    className="w-full px-2 py-2 text-xs rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-0.5 block">
                    Signature
                  </label>
                  <input
                    value={form.signature}
                    onChange={(e) =>
                      updateForm(group.vendorId, "signature", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-600 bg-gray-700 text-gray-200 focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-700 shrink-0">
          <p className="text-xs text-gray-500">
            {sending ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 border border-gray-500 border-t-orange-400 rounded-full animate-spin" />
                {progress}
              </span>
            ) : (
              `${vendorGroups.length} email${vendorGroups.length !== 1 ? "s" : ""} will be sent`
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg border border-gray-600 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || done}
              className={cls(
                "px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors",
                sending || done
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700",
              )}
            >
              {sending
                ? "Sending…"
                : done
                  ? "Sent ✓"
                  : `Send ${vendorGroups.length} Email${vendorGroups.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BulkActionsBar — floating bar shown when any orders are selected
//
// Props:
//   selectedOrderIds   Set<string>
//   orders             all order objects (to look up selected ones)
//   onClearSelection()
//   onDeliver()        — open bulk deliver modal
//   onEmail()          — open bulk email modal
//   onSiteUpdate()     — open bulk site update modal
// ─────────────────────────────────────────────────────────────────────────────
export function BulkActionsBar({
  selectedOrderIds,
  orders,
  onClearSelection,
  onDeliver,
  onEmail,
  onSiteUpdate,
}) {
  const count = selectedOrderIds.size;
  if (count === 0) return null;

  const selectedOrders = orders.filter((o) => selectedOrderIds.has(o._id));
  const deliverableCount = selectedOrders.filter((o) =>
    ["confirmed", "partial"].includes(o.status),
  ).length;
  const uniqueVendors = new Set(
    selectedOrders.map((o) => o.vendor?._id?.toString() || "__none__"),
  );
  const sameVendor = uniqueVendors.size === 1;
  const allEmailable = selectedOrders.every((o) =>
    ["pending", "confirmed", "partial"].includes(o.status),
  );
  const canBulkEmail = sameVendor && allEmailable && selectedOrders.length > 0;
  const allPending = selectedOrders.every((o) => o.status === "pending");

  // Site update: same-vendor requirement (matches bulk email), but not
  // restricted to pending — any non-void order can get an update.
  const allUpdateable = selectedOrders.every((o) => o.status !== "void");
  const canSiteUpdate =
    sameVendor && allUpdateable && selectedOrders.length > 0;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-gray-900 border border-gray-600 rounded-2xl shadow-2xl shadow-black/60">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold">
          {count}
        </span>
        <span className="text-sm text-gray-300 font-medium">
          order{count !== 1 ? "s" : ""} selected
        </span>
      </div>

      <div className="w-px h-5 bg-gray-600" />

      <button
        onClick={onDeliver}
        disabled={deliverableCount === 0}
        title={
          deliverableCount === 0
            ? "No confirmed/partial orders selected"
            : `Record delivery for ${deliverableCount} order${deliverableCount !== 1 ? "s" : ""}`
        }
        className={cls(
          "px-3 py-1.5 text-sm font-semibold rounded-lg text-white transition-colors",
          deliverableCount === 0
            ? "bg-gray-700 text-gray-500 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-700",
        )}
      >
        ✓ Mark Delivered
        {deliverableCount > 0 && deliverableCount < count && (
          <span className="ml-1 text-[10px] font-normal opacity-70">
            ({deliverableCount})
          </span>
        )}
      </button>

      <button
        onClick={onEmail}
        disabled={!canBulkEmail}
        title={
          !sameVendor
            ? "All selected orders must be from the same vendor"
            : !allPending
              ? "All selected orders must be pending"
              : `Send combined email for ${selectedOrders.length} order${selectedOrders.length !== 1 ? "s" : ""}`
        }
        className={cls(
          "px-3 py-1.5 text-sm font-semibold rounded-lg text-white transition-colors",
          !canBulkEmail
            ? "bg-gray-700 text-gray-500 cursor-not-allowed"
            : "bg-purple-600 hover:bg-purple-700",
        )}
      >
        ✉ Send Email
        {!sameVendor && selectedOrders.length > 1 && (
          <span className="ml-1 text-[10px] font-normal opacity-70">
            (mixed vendors)
          </span>
        )}
      </button>

      <button
        onClick={onSiteUpdate}
        disabled={!canSiteUpdate}
        title={
          !sameVendor
            ? "All selected orders must be from the same vendor"
            : !allUpdateable
              ? "Voided orders can't receive updates"
              : `Send a site update for ${selectedOrders.length} order${selectedOrders.length !== 1 ? "s" : ""}`
        }
        className={cls(
          "px-3 py-1.5 text-sm font-semibold rounded-lg text-white transition-colors",
          !canSiteUpdate
            ? "bg-gray-700 text-gray-500 cursor-not-allowed"
            : "bg-cyan-600 hover:bg-cyan-700",
        )}
      >
        📣 Site Update
        {!sameVendor && selectedOrders.length > 1 && (
          <span className="ml-1 text-[10px] font-normal opacity-70">
            (mixed vendors)
          </span>
        )}
      </button>

      <button
        onClick={onClearSelection}
        className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
        title="Clear selection"
      >
        ✕
      </button>
    </div>
  );
}
